import React, { useEffect } from 'react';

import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import { brandFont } from 'components/layout/brand-font';
import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import MenuTileGrid from 'components/menu-tile-grid';
import StoreSelected from 'components/store-selected';
import { TabbedHeader } from 'components/tabbed-header';
import useMediaQuery from 'hooks/use-media-query';
import { useTabbedMenuHeader } from 'hooks/use-tabbed-menu-header';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoadingState } from 'state/loading';
import { useMainMenuContext } from 'state/main-menu';
import { hasPropertyLocale } from 'utils/has-property-locale';

const Header = styled.h1`
  color: ${p => p.theme.token('text-default')};
  font: ${brandFont.hero};
  margin: auto;
  position: relative;
  text-align: center;

  ${Styles.desktop`
    margin-top: 9rem;
  `}
`;

// TODO: Figure out main padding for header
const MainMenu = () => {
  const { setLoading } = useLoadingState();
  const { getMainMenu } = useMainMenuContext();
  const { loading, data } = getMainMenu();
  const { formatMessage } = useIntl();
  const useEnableTabbedMenuBar = useFlag(LaunchDarklyFlag.ENABLE_TABBED_MENU_BAR);

  const {
    activeTab,
    isRouteOnItemPickerAndOutsideTabbedItems,
    isTabbedMenuLoading,
    tabbedHeaderItems,
  } = useTabbedMenuHeader({});

  const isMobile = useMediaQuery('mobile');
  const theme = useTheme();

  useEffect(() => {
    setLoading(!data || loading);
  }, [loading, data, setLoading]);

  return (
    <StoreSelected>
      <Helmet title={formatMessage({ id: 'mainMenu' })} />
      {useEnableTabbedMenuBar && !isRouteOnItemPickerAndOutsideTabbedItems && isMobile ? (
        <TabbedHeader
          data={tabbedHeaderItems}
          loading={loading || isTabbedMenuLoading}
          activeTab={activeTab}
        />
      ) : (
        <Header>{data && hasPropertyLocale(data, 'headerText') && data.headerText?.locale}</Header>
      )}
      {loading && (
        <LoadingContainer>
          <LoadingAnimation fillColor={theme.token('icon-active')} />
        </LoadingContainer>
      )}
      {data && <MenuTileGrid data={data.options} />}
    </StoreSelected>
  );
};

export default MainMenu;
